import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { AppLanguage } from '@/domain/models/appLanguage';
import ar from '@/l10n/locales/ar.json';
import en from '@/l10n/locales/en.json';
import ru from '@/l10n/locales/ru.json';
import uz from '@/l10n/locales/uz.json';
import type { L10nKey } from '@/l10n/types';

/**
 * Настройка i18next — замена `AppLocalizations.delegate` и
 * `flutter_localizations` из `lib/main.dart`.
 *
 * Отличий от Dart-поведения нет: все четыре локали вшиты в бандл (в Dart их
 * тоже компилировал `intl_utils`, отдельной загрузки не было), язык задаётся
 * приложением явно — определять его по браузеру нельзя, потому что киоск
 * стартует на узбекском независимо от системных настроек планшета.
 */

/** `main_locale: uz` из `pubspec.yaml` — язык по умолчанию и запасной. */
const MAIN_LOCALE = 'uz';

/** `AppLanguage.supportedLocales` — порядок как в Dart-перечислении. */
const SUPPORTED_LOCALES: readonly string[] = ['uz', 'en', 'ru', 'ar'];

/**
 * Единственное пространство имён. Файлы локалей плоские, без вложенности,
 * поэтому разделители ключей отключены ниже — ключ ищется буквально.
 */
const DEFAULT_NAMESPACE = 'translation';

const resources = {
  uz: { [DEFAULT_NAMESPACE]: uz },
  en: { [DEFAULT_NAMESPACE]: en },
  ru: { [DEFAULT_NAMESPACE]: ru },
  ar: { [DEFAULT_NAMESPACE]: ar },
};

/**
 * Код локали i18next для каждого значения `AppLanguage` — это `param` из
 * Dart-перечисления (`AppLanguage.uzbek('uz', …)`), он же ключ в [resources].
 */
const L10N_LANGUAGE_CODE: Record<AppLanguage, string> = {
  [AppLanguage.uzbek]: 'uz',
  [AppLanguage.english]: 'en',
  [AppLanguage.russian]: 'ru',
  [AppLanguage.arabic]: 'ar',
};

/** Экземпляр i18next приложения. */
export const i18n = i18next;

/**
 * Список всех ключей перевода. Берётся из основной локали, как и геттеры
 * `AppLocalizations` в Dart. Нужен, чтобы собрать объект [L10n] с полем на
 * каждый ключ.
 */
export const L10N_KEYS: readonly L10nKey[] = Object.freeze(
  Object.keys(uz) as L10nKey[],
);

/**
 * Промис инициализации. Хранится, чтобы два параллельных вызова [initI18n]
 * не запустили `init` дважды.
 */
let initPromise: Promise<void> | null = null;

async function runInit(): Promise<void> {
  await i18n.use(initReactI18next).init({
    resources,
    lng: MAIN_LOCALE,
    fallbackLng: MAIN_LOCALE,
    supportedLngs: SUPPORTED_LOCALES,
    defaultNS: DEFAULT_NAMESPACE,
    ns: [DEFAULT_NAMESPACE],
    // Значения подставляет React, экранировать их второй раз не нужно.
    interpolation: { escapeValue: false },
    // Файлы локалей плоские: ключ `error_bad_contract` не должен разбираться
    // на части по точке или двоеточию.
    keySeparator: false,
    nsSeparator: false,
    debug: false,
    react: {
      // Все переводы уже в бандле, догружать нечего. Suspense выключен,
      // чтобы рендер не требовал границы `<Suspense>`, которой во Flutter
      // не было — там `context.l10n` всегда отдавал строку сразу.
      useSuspense: false,
    },
  });
}

/**
 * Инициализация переводов. Вызывается один раз при старте приложения,
 * до первого рендера — как `AppLocalizations.load` в Dart.
 */
export function initI18n(): Promise<void> {
  if (initPromise !== null) {
    return initPromise;
  }
  const promise = runInit().catch((error: unknown) => {
    // Сбрасываем кеш промиса, чтобы повторная попытка старта (в Dart
    // `_initialize` перезапускается через 5 секунд после любой ошибки)
    // смогла проинициализировать переводы заново.
    initPromise = null;
    throw error;
  });
  initPromise = promise;
  return promise;
}

/**
 * Смена языка приложения — аналог присваивания `MaterialApp.locale` в Dart.
 * Ресурсы уже в бандле, поэтому промис завершается сразу; ждать его нужно
 * лишь тем, кто сразу после смены читает переводы вне React.
 */
export function setL10nLanguage(lang: AppLanguage): Promise<void> {
  return i18n.changeLanguage(L10N_LANGUAGE_CODE[lang]).then(() => undefined);
}
